import { MediaItem } from '../../../app/services/mediaItems';
import { normalizeNfc } from '../../../utils/text';
import { canonicalMediaKey, stripExtension } from './maTypes';

/**
 * ショット内メディアの「バリアントグループ」構築。
 *
 * タイムライン編集の下ドロワー（オーディオタグ一覧）と、
 * `_a` / `_b` バリアント切替（セグメントコントロール）で使う。
 *
 * - variation_name ごとに最新テイクへ集約
 * - variation_name / file_name のステムが `ベース名_英字` の形なら
 *   同一ベースの比較グループとしてまとめる（linkMaSequence と同じルール）
 */

/** linkMaSequence / Preview.tsx と同じ「ベース名_a」検出ルール */
const COMPARISON_SUFFIX_RE = /^(.+)_([a-zA-Z])$/;

export interface MaVariantOption {
  /** サフィックス文字（'a', 'b'...）。非比較グループは null */
  letter: string | null;
  variationName: string;
  latestTake: MediaItem;
}

export interface MaVariantGroup {
  /** グループキー（比較ベース名 or variation_name。小文字） */
  key: string;
  /** 表示ラベル（比較ベース名 or variation_name） */
  label: string;
  kind: 'audio' | 'video';
  /** サフィックス文字順。length >= 2 なら比較グループ */
  options: MaVariantOption[];
  /** グループ配下の全テイク ID（所属判定用） */
  memberIds: Set<string>;
}

/**
 * オーディオ / ビデオのメディアアイテムからバリアントグループを構築する。
 * （CSV・画像等は呼び出し側で除外して渡す）
 */
export function buildMaVariantGroups(items: MediaItem[]): MaVariantGroup[] {
  // variation_name ごとにテイクを集約（最新 = updated_at 降順の先頭）
  const byVariation = new Map<string, MediaItem[]>();
  items.forEach(item => {
    if (item.file_type !== 'audio' && item.file_type !== 'video') return;
    // NFD / NFC の表記ゆれは同じバリエーション（Preview.tsx と同じキー）
    const key = normalizeNfc(item.variation_name);
    const arr = byVariation.get(key) || [];
    arr.push(item);
    byVariation.set(key, arr);
  });

  interface VariationEntry {
    variationName: string;
    latestTake: MediaItem;
    allTakeIds: string[];
    /** 比較サフィックス検出結果（variation_name 優先 → file_name） */
    base: string | null;
    letter: string | null;
  }

  const entries: VariationEntry[] = [];
  byVariation.forEach((takes, variationName) => {
    const sorted = [...takes].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
    const latestTake = sorted[0];
    let base: string | null = null;
    let letter: string | null = null;
    for (const source of [variationName, latestTake.file_name || '']) {
      // NFC 正規化: macOS の NFD ファイル名と NFC 表記の混在でも同じベースにまとめる
      const m = stripExtension(canonicalMediaKey(source)).match(COMPARISON_SUFFIX_RE);
      if (m) {
        base = m[1];
        letter = m[2].toLowerCase();
        break;
      }
    }
    entries.push({
      variationName,
      latestTake,
      allTakeIds: sorted.map(t => t.id),
      base,
      letter,
    });
  });

  // 比較ベースごとに集約（同一ベースが 2 つ以上あれば比較グループ）
  const byBase = new Map<string, VariationEntry[]>();
  entries.forEach(entry => {
    if (entry.base === null) return;
    const arr = byBase.get(entry.base) || [];
    arr.push(entry);
    byBase.set(entry.base, arr);
  });

  const groups: MaVariantGroup[] = [];
  const grouped = new Set<VariationEntry>();

  byBase.forEach((members, base) => {
    if (members.length < 2) return; // 相方がいなければ通常グループ扱い
    members.sort((a, b) => (a.letter ?? '').localeCompare(b.letter ?? ''));
    members.forEach(m => grouped.add(m));
    groups.push({
      key: base,
      label: base,
      kind: members[0].latestTake.file_type === 'video' ? 'video' : 'audio',
      options: members.map(m => ({
        letter: m.letter,
        variationName: m.variationName,
        latestTake: m.latestTake,
      })),
      memberIds: new Set(members.flatMap(m => m.allTakeIds)),
    });
  });

  entries.forEach(entry => {
    if (grouped.has(entry)) return;
    groups.push({
      key: canonicalMediaKey(entry.variationName),
      label: entry.variationName,
      kind: entry.latestTake.file_type === 'video' ? 'video' : 'audio',
      options: [
        { letter: null, variationName: entry.variationName, latestTake: entry.latestTake },
      ],
      memberIds: new Set(entry.allTakeIds),
    });
  });

  // 表示順: ラベルの自然順
  groups.sort((a, b) => a.label.localeCompare(b.label, 'ja', { numeric: true }));
  return groups;
}

/**
 * バリアント切替オーバーライドを適用したアイテム解決関数を作る。
 *
 * overrides: グループキー → 選択した variation_name。
 * item が比較グループに属し、そのグループにオーバーライドがあれば
 * 選択バリエーションの最新テイクを返す。それ以外は item をそのまま返す。
 */
export function createVariantResolver(
  groups: MaVariantGroup[],
  overrides: Record<string, string>,
): (item: MediaItem) => MediaItem {
  // item.id → 所属グループ
  const groupByItemId = new Map<string, MaVariantGroup>();
  groups.forEach(group => {
    if (group.options.length < 2) return;
    group.memberIds.forEach(id => groupByItemId.set(id, group));
  });

  return (item: MediaItem): MediaItem => {
    const group = groupByItemId.get(item.id);
    if (!group) return item;
    const chosenName = overrides[group.key];
    if (!chosenName) return item;
    const chosen = group.options.find(o => o.variationName === chosenName);
    return chosen ? chosen.latestTake : item;
  };
}
